import React, { useState, useEffect, useCallback } from 'react';
import { ButtonBase } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Spinner, Tables } from '../../../../../../Components';
import {
  GetAllPaymentPlansByUnitId,
  UpdateUnitPaymentPlanApi,
} from '../../../../../../Services';
import {
  GetParams,
  bottomBoxComponentUpdate,
  showSuccess,
  showError,
} from '../../../../../../Helper';
import {
  PaymentPlanManagementDialog,
  PaymentPlanDeleteDialog,
} from './PaymentPlanViewUtilities';
import { propertyPaymentPlanHeaderData } from '../../../../PaymentPlans/headerData';
import { PaymentPlansLocalePath } from '../../../../PaymentPlans/i18n/PaymentPlansLocale';
import { formatPaymentPlansForTableFields } from '../../../../PaymentPlans/models';
import { PaymentPlanNotAvailableDialog } from '../../../../../../Components/PaymentPlanNotAvailableDialog';
import './UnitProfilePaymentPlanComponent.scss';

const ALLOWED_EDIT_PATHS = [
  '/home/units-sales/unit-profile-edit',
  '/home/units-lease/unit-profile-edit',
  '/home/lead-owner-units-sale/unit-profile-edit',
];

const INITIAL_FILTER = {
  pageSize: 25,
  pageIndex: 0,
};

export const UnitProfilePaymentPlanComponent = ({
  parentTranslationPath,
  translationPath,
}) => {
  const unitId = +GetParams('id');
  const { t } = useTranslation(parentTranslationPath);

  const [isLoading, setIsLoading] = useState(false);
  const [paymentPlans, setPaymentPlans] = useState({
    result: [],
    totalCount: 0,
  });
  const [checkedCardsIds, setCheckedCardsIds] = useState([]);
  const [checkedCards, setCheckedCards] = useState([]);
  const [filter, setFilter] = useState({ ...INITIAL_FILTER, unitId });
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [
    isPaymentPlanNotAvailableDialogOpen,
    setIsPaymentPlanNotAvailableDialogOpen,
  ] = useState(false);
  const isCorrectLocation = useCallback(() => {
    return ALLOWED_EDIT_PATHS.includes(window.location.pathname);
  }, []);

  const getAllPaymentTypes = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const res = await GetAllPaymentPlansByUnitId(id);
      if (res && (!res.status || res.status === 200)) {
        setPaymentPlans({
          result: formatPaymentPlansForTableFields(res),
          totalCount: 0,
        });
      } else {
        setPaymentPlans({ result: [], totalCount: 0 });
      }
    } catch (error) {
      setPaymentPlans({ result: [], totalCount: 0 });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateUnitPaymentPlanApi = useCallback(async () => {
    setIsLoading(true);
    try {
      const unitPaymentPlansList = paymentPlans.result.map((item) => ({
        propertyPaymentPlanId: item.propertyPaymentPlanId,
        isAdded:
          checkedCardsIds.includes(item.propertyPaymentPlanId) ||
          item.isSelected,
      }));

      if (unitPaymentPlansList.length > 0) {
        const body = { unitPaymentPlans: unitPaymentPlansList };
        const res = await UpdateUnitPaymentPlanApi(unitId, body);

        if (res && (!res.status || res.status === 200)) {
          showSuccess(t('payment-plan-saved-successfully'));
        } else {
          showError(t('failed-to-save-payment-plan'));
        }
      }
    } catch (error) {
      showError(t('failed-to-save-payment-plan'));
    } finally {
      setIsLoading(false);
    }
  }, [paymentPlans.result, checkedCardsIds, unitId, t]);

  const handleSelectClicked = useCallback(
    (row, rowIndex) => {
      setActiveItem(row);

      if (row?.allowSelectPaymentPlan === false) {
        setIsPaymentPlanNotAvailableDialogOpen(true);
        return;
      }

      if (!row.isSelectedUnit) {
        const updatedPlans = [...paymentPlans.result];
        updatedPlans[rowIndex].isSelectedUnit = true;
        setPaymentPlans((prev) => ({ ...prev, result: updatedPlans }));
      } else {
        updateSelectedCards(row);
      }
    },
    [paymentPlans.result]
  );

  const updateSelectedCards = useCallback((row) => {
    const paymentPlanId = row.propertyPaymentPlanId;

    setCheckedCardsIds((prev) => {
      const index = prev.indexOf(paymentPlanId);
      return index !== -1
        ? prev.filter((id) => id !== paymentPlanId)
        : [...prev, paymentPlanId];
    });

    setCheckedCards((prev) => {
      const index = prev.findIndex(
        (item) => item.propertyPaymentPlanId === paymentPlanId
      );
      return index !== -1
        ? prev.filter((item) => item.propertyPaymentPlanId !== paymentPlanId)
        : [...prev, row];
    });
  }, []);

  const getIsSelected = useCallback(
    (row) => {
      return checkedCards.some(
        (item) => item.propertyPaymentPlanId === row.propertyPaymentPlanId
      );
    },
    [checkedCards]
  );

  const handlePaymentPlanNotAvailableConfirm = useCallback(() => {
    if (!activeItem) return;

    const rowIndex = paymentPlans.result.findIndex(
      (item) => item.propertyPaymentPlanId === activeItem.propertyPaymentPlanId
    );

    if (rowIndex === -1) return;

    if (!activeItem.isSelectedUnit) {
      const updatedPlans = [...paymentPlans.result];
      updatedPlans[rowIndex].isSelectedUnit = true;
      setPaymentPlans((prev) => ({ ...prev, result: updatedPlans }));
    } else {
      updateSelectedCards(activeItem);
    }

    setIsPaymentPlanNotAvailableDialogOpen(false);
  }, [activeItem, paymentPlans.result, updateSelectedCards]);

  const onPageIndexChanged = useCallback((pageIndex) => {
    setFilter((prev) => ({ ...prev, pageIndex }));
  }, []);

  useEffect(() => {
    getAllPaymentTypes(unitId);
  }, [getAllPaymentTypes, unitId]);

  useEffect(() => {
    const selectedPlans = paymentPlans.result.filter(
      (item) => item.isSelectedUnit
    );
    setCheckedCards(selectedPlans);
    setCheckedCardsIds(selectedPlans.map((item) => item.propertyPaymentPlanId));
  }, [paymentPlans.result]);

  useEffect(() => {
    bottomBoxComponentUpdate(
      <div className='d-flex-v-center-h-end flex-wrap'>
        <ButtonBase
          className='btns theme-solid mb-2'
          onClick={updateUnitPaymentPlanApi}
        >
          <span>{t('Shared:save')}</span>
        </ButtonBase>
      </div>
    );
  }, [updateUnitPaymentPlanApi, t]);

  useEffect(() => {
    return () => bottomBoxComponentUpdate(null);
  }, []);

  return (
    <div className='view-wrapper activities-view-wrapper'>
      <Spinner isActive={isLoading} isAbsolute />

      <div className='d-flex-column mt-3'>
        <div className='w-100 px-3'>
          <Tables
            data={paymentPlans.result}
            headerData={propertyPaymentPlanHeaderData}
            defaultActions={[]}
            itemsPerPage={filter.pageSize}
            activePage={filter.pageIndex}
            translationPath={translationPath}
            parentTranslationPath={PaymentPlansLocalePath}
            translationPathFordata={translationPath}
            totalItems={paymentPlans.totalCount}
            bodyRowId={paymentPlans.propertyPaymentPlanId}
            isWithCheckboxColumn={true}
            isWithFilter={true}
            selectAllOptions={{
              selectedRows: checkedCardsIds,
              getIsSelected,
              onSelectClicked: handleSelectClicked,
              disabledRows: [],
              checkBoxesClassName: isCorrectLocation()
                ? 'payment-plan-disabled'
                : '',
            }}
            TotalSelect={checkedCardsIds.length}
            HideSellectAll
          />
        </div>
      </div>

      {/* Dialogs */}
      {openDialog && (
        <PaymentPlanManagementDialog
          open={openDialog}
          activeItem={activeItem}
          onSave={() => {
            setOpenDialog(false);
            onPageIndexChanged(0);
          }}
          close={() => setOpenDialog(false)}
          translationPath={translationPath}
          parentTranslationPath={parentTranslationPath}
        />
      )}

      {openConfirmDialog && (
        <PaymentPlanDeleteDialog
          isOpen={openConfirmDialog}
          activeItem={activeItem}
          reloadData={() => {
            setOpenConfirmDialog(false);
            onPageIndexChanged(0);
            getAllPaymentTypes(unitId);
          }}
          isOpenChanged={() => setOpenConfirmDialog(false)}
          translationPath={translationPath}
          parentTranslationPath={parentTranslationPath}
        />
      )}

      {isPaymentPlanNotAvailableDialogOpen && (
        <PaymentPlanNotAvailableDialog
          isOpen={isPaymentPlanNotAvailableDialogOpen}
          t={t}
          onSave={() => {
            handlePaymentPlanNotAvailableConfirm();
            setActiveItem(null);
          }}
          onClose={() => {
            setIsPaymentPlanNotAvailableDialogOpen(false);
            setActiveItem(null);
          }}
          translationPath={translationPath}
          parentTranslationPath={parentTranslationPath}
        />
      )}
    </div>
  );
};

UnitProfilePaymentPlanComponent.propTypes = {
  parentTranslationPath: PropTypes.string.isRequired,
  translationPath: PropTypes.string.isRequired,
};
